import { PayloadAction } from 'typesafe-actions';

import { fetch, fetchBulk, fetchList, fetchPage, remove, request, save } from '../actions';
import { FetchPayload } from '../actions/fetch';
import { FetchBulkPayload } from '../actions/fetchBulk';
import { FetchListPayload } from '../actions/fetchList';
import { FetchPagePayload } from '../actions/fetchPage';
import { RemovePayload } from '../actions/remove';
import { RequestPayload } from '../actions/request';
import { SavePayload } from '../actions/save';

import { OptionalResource, ResourceLite } from './createResource';
import { EntityBase } from './types';

// NOTE: Types usWe cast responses to use generic types on hooks like `useQuery` or `usePagination`
export type ReturnTypeFetch<E extends EntityBase> = PayloadAction<'@lib/fetch/REQUEST', FetchPayload<E>>;
export type ReturnTypeFetchBulk<E extends EntityBase> = PayloadAction<'@lib/fetchBulk/REQUEST', FetchBulkPayload<E>>;
export type ReturnTypeFetchList<E extends EntityBase> = PayloadAction<'@lib/fetchList/REQUEST', FetchListPayload<E>>;
export type ReturnTypeFetchPage<E extends EntityBase> = PayloadAction<'@lib/fetchPage/REQUEST', FetchPagePayload<E>>;
export type ReturnTypeRemove<E extends EntityBase> = PayloadAction<'@lib/remove/REQUEST', RemovePayload<E>>;
export type ReturnTypeSave<E extends EntityBase> = PayloadAction<'@lib/save/REQUEST', SavePayload<E>>;
export type ReturnTypeRequest<E extends EntityBase> = PayloadAction<'@lib/request/REQUEST', RequestPayload<E>>;

export interface ResourceCommonActions<E extends EntityBase> {
  fetch: (payload: OptionalResource<FetchPayload<E>>) => ReturnTypeFetch<E>;
  fetchBulk: (payload: OptionalResource<FetchBulkPayload<E>>) => ReturnTypeFetchBulk<E>;
  fetchList: (payload?: OptionalResource<FetchListPayload<E>>) => ReturnTypeFetchList<E>;
  fetchPage: (payload: OptionalResource<FetchPagePayload<E>>) => ReturnTypeFetchPage<E>;
  remove: (payload: OptionalResource<RemovePayload<E>>) => ReturnTypeRemove<E>;
  save: (payload: OptionalResource<SavePayload<E>>) => ReturnTypeSave<E>;
  request: (payload: OptionalResource<RequestPayload<E>>) => ReturnTypeRequest<E>;
}

/**
 * Create common actions for a resource
 *
 * @param resource resource we want to link the actions to
 *
 * @returns Object with actions
 */
const createResourceCommonActions = <E extends EntityBase>(resource: ResourceLite<E>): ResourceCommonActions<E> => ({
  fetch: (payload: OptionalResource<FetchPayload<E>>) => fetch.request({ resource, ...payload }),
  fetchBulk: (payload: OptionalResource<FetchBulkPayload<E>>) => fetchBulk.request({ resource, ...payload }),
  fetchList: (payload: OptionalResource<FetchListPayload<E>> | undefined = {}) =>
    fetchList.request({ resource, ...payload }),
  fetchPage: (payload: OptionalResource<FetchPagePayload<E>>) => fetchPage.request({ resource, ...payload }),
  remove: (payload: OptionalResource<RemovePayload<E>>) => remove.request({ resource, ...payload }),
  save: (payload: OptionalResource<SavePayload<E>>) => save.request({ resource, ...payload }),
  request: (payload: OptionalResource<RequestPayload<E>>) => request.request({ resource, ...payload }),
});

export default createResourceCommonActions;
